/**
 * iOS In-App Purchase utility using cordova-plugin-purchase (StoreKit 2)
 * Only used on native iOS — web uses Stripe.
 * 
 * App Store Connect product IDs:
 *   - com.fashiondisc.basic.monthly   ($7.99/month auto-renewable)
 *   - com.fashiondisc.premium.monthly ($14.99/month auto-renewable)
 */
import { Capacitor } from '@capacitor/core';

const PRODUCT_IDS = {
  'basic-monthly': 'com.fashiondisc.basic.monthly',
  'premium-monthly': 'com.fashiondisc.premium.monthly',
  'storage-tier1': 'com.fashiondisc.storage.tier1',
  'storage-tier2': 'com.fashiondisc.storage.tier2',
  'bulk-topup': 'com.fashiondisc.bulk.topup',
};

let storeReady = false;
let storeInstance = null;
let verifyCallback = null;
let pendingVerification = null;

/**
 * Check if we should use iOS IAP instead of Stripe
 */
export function shouldUseStoreKit() {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios';
}

/**
 * Initialize the store — call once on app startup (iOS only)
 */
export async function initializeStore(onPurchaseVerified) {
  if (!shouldUseStoreKit()) return;
  
  const CdvPurchase = window.CdvPurchase;
  if (!CdvPurchase) {
    console.warn('⚠️ CdvPurchase not available — IAP plugin not loaded');
    return;
  }

  storeInstance = CdvPurchase.store;
  const { ProductType, Platform } = CdvPurchase;

  // Register subscription products
  storeInstance.register([
    {
      id: PRODUCT_IDS['basic-monthly'],
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
    {
      id: PRODUCT_IDS['premium-monthly'],
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
    {
      id: PRODUCT_IDS['storage-tier1'],
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
    {
      id: PRODUCT_IDS['storage-tier2'],
      type: ProductType.PAID_SUBSCRIPTION,
      platform: Platform.APPLE_APPSTORE,
    },
    {
      id: PRODUCT_IDS['bulk-topup'],
      type: ProductType.CONSUMABLE,
      platform: Platform.APPLE_APPSTORE,
    },
  ]);

  // When a purchase is approved, verify it on our backend
  storeInstance.when()
    .approved(async (transaction) => {
      console.log('✅ IAP approved:', transaction.transactionId);
      try {
        if (onPurchaseVerified) {
          await onPurchaseVerified(transaction);
        }
        transaction.finish();
        console.log('✅ IAP verified and finished:', transaction.transactionId);
        // Resolve any pending verification promise
        if (pendingVerification) {
          pendingVerification.resolve(transaction);
          pendingVerification = null;
        }
      } catch (err) {
        console.error('❌ Failed to verify purchase:', err);
        if (pendingVerification) {
          pendingVerification.reject(err);
          pendingVerification = null;
        }
      }
    })
    .finished((transaction) => {
      console.log('✅ IAP finished:', transaction.transactionId);
    });

  // Handle errors
  storeInstance.error((err) => {
    console.error('❌ Store error:', err.code, err.message);
  });

  await storeInstance.initialize([Platform.APPLE_APPSTORE]);
  storeReady = true;
  console.log('✅ IAP Store initialized');
}

/**
 * Get product info (price, title) from App Store
 */
export function getProduct(planId) {
  if (!storeInstance || !storeReady) return null;
  const productId = PRODUCT_IDS[planId];
  if (!productId) return null;
  return storeInstance.get(productId);
}

/**
 * Get the localized price string from the App Store
 */
export function getLocalizedPrice(planId) {
  const product = getProduct(planId);
  if (!product?.pricing) return null;
  return product.pricing.price;
}

/**
 * Initiate a purchase through StoreKit and wait for backend verification
 */
export async function purchaseProduct(planId) {
  if (!storeInstance || !storeReady) {
    throw new Error('Store not initialized');
  }

  const productId = PRODUCT_IDS[planId];
  if (!productId) {
    throw new Error(`Unknown plan: ${planId}`);
  }

  const product = storeInstance.get(productId);
  if (!product) {
    throw new Error(`Product not found in store: ${productId}`);
  }

  const offer = product.getOffer();
  if (!offer) {
    throw new Error('No offer available for this product');
  }

  // Create a promise that will resolve when the .approved() callback finishes verification
  const verificationPromise = new Promise((resolve, reject) => {
    pendingVerification = { resolve, reject };
    // Timeout after 15 seconds
    setTimeout(() => {
      if (pendingVerification) {
        pendingVerification = null;
        reject(new Error('Verification timed out — please try Restore Purchases'));
      }
    }, 15000);
  });

  // Triggers the native StoreKit purchase sheet
  const result = await storeInstance.order(offer);
  
  if (result?.isError) {
    pendingVerification = null;
    if (result.code === window.CdvPurchase?.ErrorCode?.PAYMENT_CANCELLED) {
      throw new Error('cancelled');
    }
    throw new Error(result.message || 'Purchase failed');
  }
  
  // Wait for the .approved() callback to verify with backend
  await verificationPromise;
  return result;
}

/**
 * Restore previous purchases — Apple requires a visible Restore button (Guideline 3.1.2)
 */
export async function restorePurchases() {
  if (!storeInstance || !storeReady) {
    throw new Error('Store not initialized');
  }

  // Create a promise that will resolve when the .approved() callback finishes verification
  const verificationPromise = new Promise((resolve, reject) => {
    pendingVerification = { resolve, reject };
    // Timeout after 15 seconds — restore may have nothing to restore
    setTimeout(() => {
      if (pendingVerification) {
        pendingVerification = null;
        resolve(null); // resolve with null = no purchases found to restore
      }
    }, 15000);
  });

  await storeInstance.restorePurchases();
  return verificationPromise;
}

/**
 * Open iOS subscription management (Settings > Apple ID > Subscriptions)
 */
export async function openManageSubscriptions() {
  if (!storeInstance) return;
  try {
    await storeInstance.manageSubscriptions();
  } catch {
    // Fallback: open the App Store subscriptions URL
    window.open('https://apps.apple.com/account/subscriptions', '_blank');
  }
}

/**
 * Verify a transaction with our backend
 */
export async function verifyWithBackend(transaction, apiUrl) {
  const token = localStorage.getItem('token');
  if (!token) throw new Error('Not authenticated');

  const response = await fetch(`${apiUrl}/subscription/apple-verify`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    },
    body: JSON.stringify({
      transactionId: transaction.transactionId,
      productId: transaction.products?.[0]?.id || transaction.productId,
      receipt: transaction.receipt || transaction.appStoreReceipt,
    }),
  });

  if (!response.ok) {
    const err = await response.json();
    throw new Error(err.error || 'Verification failed');
  }

  return response.json();
}
